import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const DISMISS_KEY = 'lucent_hr_widget_dismissed';

/**
 * A dismissible, tooth-shaped floating widget that lets regular employees
 * jump straight to "My Requests" and auto-open the "Other Request" form.
 * Hidden for admin/GM/managers/IT/contracted workers and on public pages.
 */
export default function FloatingHRWidget() {
  const { user, isAdmin, isGM } = useAuth();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = language === 'ar';

  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);

  // Auto-expand briefly on first mount so users notice the widget,
  // then collapse to a small tooth. This runs once per session.
  useEffect(() => {
    if (dismissed || !user) return;
    setExpanded(true);
    const t = setTimeout(() => setExpanded(false), 3500);
    return () => clearTimeout(t);
  }, [dismissed, user]);

  // Guard rails — never render on public routes or for privileged users.
  if (!user) return null;
  if (isAdmin || isGM) return null;
  if (user.is_department_manager || user.is_finance_manager || user.is_it_manager) return null;
  if (user.is_contracted_worker) return null;

  const path = location.pathname || '';
  if (path === '/' || path.startsWith('/login') || path.startsWith('/verify')) return null;
  // Avoid stacking on top of the form dialog on the requests page itself.
  if (path.startsWith('/employee/requests')) return null;

  if (dismissed) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleClick = () => {
    navigate('/employee/requests', { state: { openNew: 'other' } });
  };

  const label = isAr ? 'اسأل مختص شؤون الموظفين' : 'Ask HR Specialist';
  const sideClass = isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6';

  return (
    <div
      className={`fixed bottom-24 sm:bottom-8 ${sideClass} z-[60] select-none`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="floating-hr-widget"
    >
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        aria-label={label}
        className={[
          'group relative flex items-center gap-2 pl-2 pr-2 py-2',
          'bg-gradient-to-br from-[#7C6FE0] via-[#655dc6] to-[#4E45A6]',
          'text-white shadow-2xl shadow-[#655dc6]/40',
          'border border-white/30 backdrop-blur-sm',
          'transition-all duration-300 ease-out',
          'hover:scale-[1.03] active:scale-95',
          expanded ? 'rounded-full pr-4' : 'rounded-full',
        ].join(' ')}
        data-testid="floating-hr-widget-btn"
      >
        {/* Pulsing halo — a tooth-shaped, dental-themed ping */}
        <span className="absolute inset-0 rounded-full bg-[#655dc6] opacity-30 animate-ping pointer-events-none" />
        {/* Cartoon tooth wearing a red-and-white shemagh + black igal.
            Sits inside a white "cap" to keep it readable on the purple pill. */}
        <span className="relative flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-inner shrink-0">
          <svg viewBox="0 0 64 64" width="30" height="30" aria-hidden="true">
            <defs>
              <pattern id="lc-shemagh" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
                <rect width="5" height="5" fill="#ffffff" />
                <path d="M0 0 L5 0 M0 5 L5 5" stroke="#d1122d" strokeWidth="1.4" />
                <path d="M0 0 L0 5 M5 0 L5 5" stroke="#d1122d" strokeWidth="1.4" />
              </pattern>
            </defs>
            {/* Tooth body */}
            <path
              d="M18 32 C18 24, 24 20, 32 20 C40 20, 46 24, 46 32 C46 42, 42 54, 37 54 C34 54, 34 46, 32 46 C30 46, 30 54, 27 54 C22 54, 18 42, 18 32 Z"
              fill="#ffffff"
              stroke="#655dc6"
              strokeWidth="1.5"
            />
            {/* Eyes */}
            <circle cx="27" cy="36" r="1.6" fill="#2c2668" />
            <circle cx="37" cy="36" r="1.6" fill="#2c2668" />
            {/* Smile */}
            <path d="M27 42 Q32 46 37 42" stroke="#2c2668" strokeWidth="1.4" fill="none" strokeLinecap="round" />
            {/* Shemagh drape over the head */}
            <path
              d="M12 26 C14 10, 26 6, 32 6 C38 6, 50 10, 52 26 C48 22, 42 20, 32 20 C22 20, 16 22, 12 26 Z"
              fill="url(#lc-shemagh)"
              stroke="#8a0f22"
              strokeWidth="1"
            />
            {/* Right side flap */}
            <path
              d="M50 22 C54 30, 52 40, 46 42 C48 34, 48 28, 48 24 Z"
              fill="url(#lc-shemagh)"
              stroke="#8a0f22"
              strokeWidth="0.8"
            />
            {/* Igal (black rope) */}
            <ellipse cx="32" cy="10" rx="18" ry="2.5" fill="#000" />
          </svg>
        </span>
        {/* Expanding label */}
        <span
          className={[
            'relative overflow-hidden whitespace-nowrap font-semibold text-sm',
            'transition-[max-width,opacity,margin] duration-300 ease-out',
            expanded ? 'max-w-[260px] opacity-100 mx-1' : 'max-w-0 opacity-0 mx-0',
          ].join(' ')}
        >
          {label}
        </span>
      </button>

      {/* Dismiss (X) — small, top corner of the widget */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={isAr ? 'إخفاء' : 'Dismiss'}
        className={[
          'absolute -top-1 flex items-center justify-center w-6 h-6 rounded-full',
          'bg-white text-[#655dc6] border border-[#C4BFF0] shadow-md',
          'hover:bg-[#F2F0FA] hover:scale-110 transition-all',
          isRTL ? '-right-1' : '-left-1',
        ].join(' ')}
        data-testid="floating-hr-widget-dismiss"
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}
